/*
  Product page behaviour: two small elements, both leaning on Dawn.

  <ilura-variant-sync> keeps ILURA's own markup in step with a flavour change.

  Dawn's <product-info> already does the hard part — it fetches the re-rendered
  section, swaps the price, the submit button and the media gallery, and
  rewrites the URL. What it does NOT do is update markup Dawn has never heard
  of, and it only touches a fixed list of ids (price, Sku, Inventory, Volume,
  Price-Per-Item). The pack shot on its flavour ground, the flavour name and
  the sticky bar are all ours.

  So rather than re-implementing the fetch, this element subscribes to the
  variantChange event Dawn publishes at the end of that same request and
  updates its own nodes from the HTML Dawn already downloaded. One network
  request, Dawn's, and no second source of truth for which variant is current.

  It is deliberately scoped to its own subtree rather than the document, so the
  buy box and the sticky bar each own their own updates and neither has to know
  the other exists.

  <ilura-pdp-gallery> makes the thumbnails swap the hero. It changes only the
  hero <img>'s src, srcset and alt, so the flavour ground behind it and the
  sold-out badge beside it are untouched. A flavour change re-renders the hero
  through <ilura-variant-sync>, so the thumbnail selection is cleared then.

  <ilura-tabs> is the label panel: active ingredients, directions, warnings —
  tabs from 750px up, an accordion below it.
  A custom element because connectedCallback re-fires when the theme editor
  re-renders the section, where a DOMContentLoaded pass would leave dead tabs.
*/

if (!customElements.get('ilura-variant-sync')) {
  class IluraVariantSync extends HTMLElement {
    connectedCallback() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (!data || !data.html) return;
        this.sync(data.html);
      });
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    sync(html) {
      /* Content: swap the inner markup from the node with the same id. */
      this.querySelectorAll('[data-variant-sync]').forEach((node) => {
        if (!node.id) return;
        const source = html.getElementById(node.id);
        if (source) node.innerHTML = source.innerHTML;
      });

      /*
        Appearance: the flavour ground is an inline custom property rather than
        a class, because the colour comes from a variant metafield and could be
        anything. Copying the whole style attribute keeps that one source.
      */
      this.querySelectorAll('[data-variant-style]').forEach((node) => {
        if (!node.id) return;
        const source = html.getElementById(node.id);
        if (source) node.setAttribute('style', source.getAttribute('style') || '');
      });
    }
  }

  customElements.define('ilura-variant-sync', IluraVariantSync);
}

if (!customElements.get('ilura-tabs')) {
  class IluraTabs extends HTMLElement {
    connectedCallback() {
      /*
        The phone accordion. Each header opens and closes its own panel and
        leaves the others alone, like every other accordion on the site. The
        CSS shows a panel below 750px from its header's aria-expanded, so the
        tab state and the accordion state never have to agree.
      */
      this.querySelectorAll('.ilura-lbl__acc').forEach((header) => {
        header.addEventListener('click', () => {
          const open = header.getAttribute('aria-expanded') === 'true';
          header.setAttribute('aria-expanded', String(!open));
        });
      });

      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      if (!this.tabs.length) return;

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab));
      });

      /* Arrow keys move between tabs, which is what the tablist pattern
         requires — without it the panel is reachable but not navigable. */
      this.addEventListener('keydown', (event) => {
        const index = this.tabs.indexOf(document.activeElement);
        if (index === -1) return;

        let next = null;
        if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabs.length - 1;
        if (next === null) return;

        event.preventDefault();
        this.tabs[next].focus();
        this.select(this.tabs[next]);
      });
    }

    select(tab) {
      this.tabs.forEach((other) => {
        const selected = other === tab;
        other.setAttribute('aria-selected', String(selected));
        other.setAttribute('tabindex', selected ? '0' : '-1');

        const panel = this.querySelector(`#${other.getAttribute('aria-controls')}`);
        if (panel) panel.hidden = !selected;
      });
    }
  }

  customElements.define('ilura-tabs', IluraTabs);
}

if (!customElements.get('ilura-pdp-gallery')) {
  class IluraPdpGallery extends HTMLElement {
    connectedCallback() {
      this.thumbs = Array.from(this.querySelectorAll('.ilura-pdp__thumb'));
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.show(thumb));
      });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () => this.mark(null));
      }
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    show(thumb) {
      const img = this.querySelector('.ilura-pdp__hero img');
      if (!img) return;
      // The phone-sized <source> would otherwise keep showing the first photo.
      img.closest('picture')?.querySelectorAll('source').forEach((source) => source.remove());

      img.srcset = thumb.dataset.srcset;
      img.src = thumb.dataset.src;
      img.alt = thumb.dataset.alt;
      this.mark(thumb);
    }

    mark(current) {
      this.thumbs.forEach((thumb) => {
        thumb.setAttribute('aria-pressed', String(thumb === current));
      });
    }
  }

  customElements.define('ilura-pdp-gallery', IluraPdpGallery);
}
